import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  Heart,
  ShoppingCart,
  Building,
  User,
  ShieldUser,
  Motorbike,
  Truck,
  Bell,
  Gavel,
  X,
} from "lucide-react";
import { useSearch } from "../context/SearchContext";
import { useCart } from "../context/CartContext";
import UserMenu from "./UserMenu";
import API from "../api/axiosConfig";

export default function Navbar() {
  const location = useLocation();
  const { showSearch, setShowSearch, search, setSearch } = useSearch();

  let user = null;
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser && storedUser !== "undefined") {
      user = JSON.parse(storedUser);
    }
  } catch (error) {
    console.error("Error parsing user:", error);
    localStorage.removeItem("user");
  }

  const [open, setOpen] = useState(false);
  const [cartDismissed, setCartDismissed] = useState(false);
  const navigate = useNavigate();
  const { cart } = useCart();
  const cartCount = cart.length;

  const prevCartCount = useRef(cartCount);
  useEffect(() => {
    if (prevCartCount.current === 0 && cartCount > 0) setCartDismissed(false);
    prevCartCount.current = cartCount;
  }, [cartCount]);

  useEffect(() => {
    setShowSearch(false);
    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    if (!showSearch) return;
    const handleEsc = (e) => { if (e.key === "Escape") setShowSearch(false); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
    // eslint-disable-next-line
  }, [showSearch]);

  function NotificationsBell() {
    const [count, setCount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
      const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
      if (!token) return;
      API.get("/notifications/unread-count")
        .then((res) => setCount(res.data?.data?.count ?? res.data?.count ?? 0))
        .catch(() => {});
    }, []);

    return (
      <button
        className="relative hover:text-supernova-400 transition"
        onClick={() => navigate("/notifications")}
      >
        <Bell size={22} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
            {count}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {/* ── Navbar ─────────────────────────────────────────────────────────
          safe-area-inset-top empuja el contenido por debajo de la barra
          de estado del sistema operativo (reloj, wifi, batería)
          ─────────────────────────────────────────────────────────────── */}
      <nav
        className="bg-gray-900 shadow-md sticky top-0 z-50 w-full"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          // backdrop-blur + sticky es una combinación con bugs conocidos en el WebView de
          // Android/Chromium: el header se puede "perder" durante scroll rápido y dejar
          // que el contenido se dibuje encima. transform fuerza su propia capa de
          // composición para que el navegador no la recalcule junto con el contenido.
          transform: "translateZ(0)",
          willChange: "transform",
        }}
      >
        {/* Left safe-area padding for landscape notch on some phones */}
        <div
          className="max-w-7xl mx-auto flex justify-between items-center"
          style={{
            paddingLeft:  "max(1.25rem, env(safe-area-inset-left, 0px))",
            paddingRight: "max(1.25rem, env(safe-area-inset-right, 0px))",
            paddingTop: "0.625rem",
            paddingBottom: "0.625rem",
          }}
        >
          <Link to="/" className="flex-shrink-0">
            <img
              src="/navbar.png"
              alt="Qkiere"
              className="h-9 w-auto object-contain"
            />
          </Link>

          {/* Mobile: bell + search */}
          <div className="lg:hidden flex items-center gap-1 text-white">
            {user && <NotificationsBell />}
            <button
              className="p-2 rounded-md hover:bg-gray-800 transition"
              onClick={() => setShowSearch(true)}
            >
              <Search size={22} />
            </button>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6 text-white">
            {user ? (
              <>
                <span className="opacity-90 text-sm">
                  Hola, <span className="font-semibold">{user.name}</span>{" "}
                  <span className="opacity-60">({user.role})</span>
                </span>

                <NotificationsBell />

                <button onClick={() => navigate("/favorites")} className="hover:text-supernova-400 transition">
                  <Heart size={22} />
                </button>

                <Link to="/auctions" className="hover:text-supernova-400 transition" title="Subastas">
                  <Gavel size={22} />
                </Link>

                {user.role === "company" && (
                  <>
                    <button onClick={() => navigate("/company")} className="hover:text-supernova-400 transition" title="Panel empresa">
                      <Building size={22} />
                    </button>
                    <button onClick={() => navigate("/myaccount")} className="hover:text-supernova-400 transition">
                      <User size={22} />
                    </button>
                  </>
                )}

                {user.role === "admin" && (
                  <>
                    <button onClick={() => navigate("/admin")} className="hover:text-supernova-400 transition" title="Admin">
                      <ShieldUser size={22} />
                    </button>
                    <button onClick={() => navigate("/myaccount")} className="hover:text-supernova-400 transition">
                      <User size={22} />
                    </button>
                  </>
                )}

                {user.role === "user" && (
                  <>
                    {user.companyId && (
                      <button onClick={() => navigate("/comanda")} className="hover:text-supernova-400 transition" title="Comanda">
                        <ShoppingCart size={22} />
                      </button>
                    )}
                    <button onClick={() => navigate("/myaccount")} className="hover:text-supernova-400 transition">
                      <User size={22} />
                    </button>
                  </>
                )}

                {user.role === "ryder" && (
                  <>
                    <button
                      onClick={() => navigate("/ryder")}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-supernova-500/15 border border-supernova-500/30 text-supernova-400 hover:bg-supernova-500/25 transition text-sm font-semibold"
                      title="Panel Domiciliario"
                    >
                      <Motorbike size={18} />
                      Panel Ryder
                    </button>
                    <button onClick={() => navigate("/myaccount")} className="hover:text-supernova-400 transition">
                      <User size={22} />
                    </button>
                  </>
                )}

                {user.role === "delivery" && (
                  <>
                    <button
                      onClick={() => navigate("/delivery")}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-500/15 border border-blue-500/30 text-blue-400 hover:bg-blue-500/25 transition text-sm font-semibold"
                      title="Panel Empresa Delivery"
                    >
                      <Truck size={18} />
                      Panel Delivery
                    </button>
                    <button onClick={() => navigate("/myaccount")} className="hover:text-supernova-400 transition">
                      <User size={22} />
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/login" className="hover:text-supernova-400 font-medium transition text-sm">
                  Ingresar
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-supernova-500 hover:bg-supernova-600 text-white font-semibold rounded-xl transition text-sm"
                >
                  Registrarse
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {open && (
        <div className="absolute top-full right-4 mt-2 bg-white rounded-2xl shadow-xl p-4 z-50">
          <UserMenu user={user} onClose={() => setOpen(false)} />
        </div>
      )}

      {/* Carrito flotante */}
      {cartCount > 0 && !cartDismissed && (
        <motion.div
          drag
          dragMomentum={false}
          dragElastic={0.08}
          dragConstraints={{
            top: 0,
            bottom: window.innerHeight - 120,
            left: -(window.innerWidth - 140),
            right: 0,
          }}
          className="fixed top-[calc(env(safe-area-inset-top,0px)+4.5rem)] right-4 z-40 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="flex items-center gap-1 bg-supernova-400 text-black font-bold text-sm pl-3 pr-1 py-1.5 rounded-2xl shadow-lg">
            <button onClick={() => navigate("/cart")} className="flex items-center gap-2 pr-1">
              <ShoppingCart size={16} />
              <span>{cartCount}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setCartDismissed(true); }}
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 transition flex-shrink-0"
            >
              <X size={11} />
            </button>
          </div>
        </motion.div>
      )}

      {/* Search overlay */}
      {showSearch && (
        <div
          className="fixed inset-0 z-[10000] bg-black/50"
          onClick={() => setShowSearch(false)}
        >
          <div
            className="absolute left-1/2 -translate-x-1/2 w-[92%] max-w-xl bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 p-3"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Search size={18} className="text-gray-400 shrink-0 ml-1" />
              <input
                autoFocus
                type="text"
                placeholder="Buscar productos o comercios..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent px-2 py-2.5 text-white placeholder-gray-500 focus:outline-none text-sm"
                onBlur={() => { if (!search.trim()) setShowSearch(false); }}
              />
              <button
                onClick={() => setShowSearch(false)}
                className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
